import {
  addDoc,
  collection,
  getDocs,
  limit,
  query,
  where,
} from 'firebase/firestore'
import { firestore } from '@core/services/firebase/firebase-service'
import { CoreError, CoreErrorCause } from '@domain/models/core-error'
import {
  NewsletterPeriodicity,
  type CreateNewsletterSubscriptionInput,
  type NewsletterPeriodicityValue,
} from '@domain/models/newsletter'

const NEWSLETTER_COLLECTION = 'newsletter_subscriptions'
const NEWSLETTER_SOURCE = 'parkinsons_insights_web'
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const TIMEZONE_MAX_LENGTH = 120

function normalizeEmail(email: string): string {
  return email.trim().toLowerCase()
}

function normalizeTimeZone(timezone: string): string {
  return timezone.trim().slice(0, TIMEZONE_MAX_LENGTH)
}

function isNewsletterPeriodicity(value: string): value is NewsletterPeriodicityValue {
  return (
    value === NewsletterPeriodicity.Daily ||
    value === NewsletterPeriodicity.Weekly ||
    value === NewsletterPeriodicity.Monthly
  )
}

function getNewsletterCollection() {
  if (!firestore) {
    throw new CoreError({
      cause: CoreErrorCause.Unknown,
      context: 'Firestore is not initialized.',
    })
  }

  return collection(firestore, NEWSLETTER_COLLECTION)
}

export class NewsletterService {
  private static instance: NewsletterService

  private constructor() {}

  public static getInstance(): NewsletterService {
    if (!NewsletterService.instance) {
      NewsletterService.instance = new NewsletterService()
    }
    return NewsletterService.instance
  }

  public async subscribeToNewsletter(
    input: CreateNewsletterSubscriptionInput,
  ): Promise<string> {
    const email = normalizeEmail(input.email)
    const periodicity = input.periodicity
    const timezone = normalizeTimeZone(input.timezone)

    if (!EMAIL_REGEX.test(email)) {
      throw new CoreError({
        cause: CoreErrorCause.InvalidCredential,
        context: 'Please provide a valid email address.',
      })
    }

    if (!isNewsletterPeriodicity(periodicity)) {
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: `Invalid newsletter periodicity "${periodicity}".`,
      })
    }

    if (timezone.length === 0) {
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Newsletter timezone is required.',
      })
    }

    try {
      const existingSubscriptionSnapshot = await getDocs(
        query(getNewsletterCollection(), where('email', '==', email), limit(1)),
      )

      if (!existingSubscriptionSnapshot.empty) {
        throw new CoreError({
          cause: CoreErrorCause.EmailAlreadyInUse,
          context: 'This email is already subscribed to the newsletter.',
        })
      }

      const createdDoc = await addDoc(getNewsletterCollection(), {
        email,
        created_at: new Date(),
        periodicity,
        timezone,
        source: NEWSLETTER_SOURCE,
      })

      return createdDoc.id
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }

      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Failed to subscribe to the newsletter.',
        underlyingException: error,
      })
    }
  }
}

const newsletterService = NewsletterService.getInstance()

export async function subscribeToNewsletter(
  input: CreateNewsletterSubscriptionInput,
): Promise<string> {
  return newsletterService.subscribeToNewsletter(input)
}
